import { useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Gift, Mail, Lock, User as UserIcon, Phone, Eye, EyeOff } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const inputCls = "w-full h-12 pl-11 pr-4 rounded-xl border border-stone-200 bg-white text-sm outline-none focus:border-[#B45309] focus:ring-2 focus:ring-[#B45309]/10";

function Field({ icon: Icon, right, ...props }) {
  return (
    <div className="relative">
      <Icon size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
      <input {...props} className={inputCls} />
      {right}
    </div>
  );
}

export default function AuthPage() {
  const { user, login, register } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const resetToken = params.get("reset");
  const [mode, setMode] = useState(resetToken ? "reset" : "login");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", confirm_password: "" });
  const [resetPw, setResetPw] = useState("");

  const from = location.state?.from || "/";
  if (user) {
    nav(from, { replace: true });
    return null;
  }

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Welcome back to Customize Gift Online!");
        nav(from, { replace: true });
      } else if (mode === "register") {
        await register(form);
        toast.success("Welcome to Customize Gift Online!");
        nav(from, { replace: true });
      } else if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { email: form.email });
        toast.success(data.message);
        setMode("login");
      } else if (mode === "reset") {
        const { data } = await api.post("/auth/reset-password", { token: resetToken, password: resetPw });
        toast.success(data.message);
        nav("/auth", { replace: true });
        setMode("login");
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] grid grid-cols-1 lg:grid-cols-2" data-testid="auth-page">
      <div className="hidden lg:flex relative overflow-hidden bg-[#1C1917] text-[#FDFBF7] flex-col justify-end p-12">
        <img src="https://images.pexels.com/photos/19258787/pexels-photo-19258787.jpeg?auto=compress&cs=tinysrgb&w=1000" alt="Gift box with ribbon" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="relative">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#E0A96D]">Customize Gift Online</p>
          <h2 className="font-serif text-4xl font-bold mt-4 leading-tight">Your photos. Your words.<br />Beautifully printed.</h2>
          <p className="text-stone-300 text-sm mt-4 max-w-sm leading-relaxed">Sign in to track orders, save wishlists, reuse addresses and check out faster.</p>
        </div>
      </div>

      <div className="flex items-center justify-center px-4 py-14">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 justify-center lg:hidden mb-8">
            <span className="w-9 h-9 rounded-xl bg-[#B45309] text-white grid place-items-center"><Gift size={18} /></span>
            <span className="font-serif text-xl font-bold">Customize Gift <span className="text-[#B45309]">Online</span></span>
          </Link>

          {mode === "reset" ? (
            <>
              <h1 className="font-serif text-3xl font-bold text-stone-900">Set a new password</h1>
              <p className="text-sm text-stone-500 mt-2">Choose something strong and memorable.</p>
              <form onSubmit={submit} className="space-y-4 mt-8">
                <Field icon={Lock} type="password" required minLength={6} value={resetPw} onChange={(e) => setResetPw(e.target.value)} placeholder="New password (min 6 characters)" data-testid="reset-password-input" />
                <button disabled={busy} className="w-full h-12 rounded-full bg-[#B45309] text-white text-sm font-bold hover:bg-[#92400E] transition-colors disabled:opacity-60" data-testid="reset-submit">{busy ? "Updating…" : "Update Password"}</button>
              </form>
            </>
          ) : mode === "forgot" ? (
            <>
              <h1 className="font-serif text-3xl font-bold text-stone-900">Forgot your password?</h1>
              <p className="text-sm text-stone-500 mt-2">Enter your email and we'll create a reset link for you.</p>
              <form onSubmit={submit} className="space-y-4 mt-8">
                <Field icon={Mail} type="email" required value={form.email} onChange={set("email")} placeholder="Email address" data-testid="forgot-email" />
                <button disabled={busy} className="w-full h-12 rounded-full bg-[#B45309] text-white text-sm font-bold hover:bg-[#92400E] transition-colors disabled:opacity-60" data-testid="forgot-submit">{busy ? "Sending…" : "Send Reset Link"}</button>
              </form>
              <button onClick={() => setMode("login")} className="text-sm font-semibold text-[#B45309] mt-5 hover:underline" data-testid="back-to-login">← Back to sign in</button>
            </>
          ) : (
            <>
              <div className="flex bg-[#F5EFE6] rounded-full p-1 mb-8" data-testid="auth-tabs">
                {["login", "register"].map((m) => (
                  <button key={m} onClick={() => setMode(m)} className={`flex-1 h-10 rounded-full text-sm font-bold transition-colors ${mode === m ? "bg-white text-stone-900 shadow-sm" : "text-stone-500"}`} data-testid={`auth-tab-${m}`}>
                    {m === "login" ? "Sign In" : "Create Account"}
                  </button>
                ))}
              </div>
              <h1 className="font-serif text-3xl font-bold text-stone-900">{mode === "login" ? "Welcome back" : "Create your account"}</h1>
              <p className="text-sm text-stone-500 mt-2">{mode === "login" ? "Your saved gifts and orders are waiting." : "It takes less than a minute to start gifting."}</p>

              <form onSubmit={submit} className="space-y-3.5 mt-8" data-testid="auth-form">
                {mode === "register" && (
                  <>
                    <Field icon={UserIcon} required minLength={2} value={form.name} onChange={set("name")} placeholder="Full name" data-testid="register-name" />
                    <Field icon={Phone} required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} placeholder="10-digit mobile number" inputMode="numeric" data-testid="register-phone" />
                  </>
                )}
                <Field icon={Mail} type="email" required value={form.email} onChange={set("email")} placeholder="Email address" data-testid="auth-email" />
                <Field
                  icon={Lock} type={showPw ? "text" : "password"} required minLength={6} value={form.password} onChange={set("password")} placeholder="Password" data-testid="auth-password"
                  right={<button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600" aria-label="Toggle password">{showPw ? <EyeOff size={15} /> : <Eye size={15} />}</button>}
                />
                {mode === "register" && <Field icon={Lock} type="password" required value={form.confirm_password} onChange={set("confirm_password")} placeholder="Confirm password" data-testid="register-confirm" />}
                {mode === "login" && (
                  <div className="text-right">
                    <button type="button" onClick={() => setMode("forgot")} className="text-xs font-semibold text-[#B45309] hover:underline" data-testid="forgot-link">Forgot password?</button>
                  </div>
                )}
                <button disabled={busy} className="w-full h-12 rounded-full bg-[#B45309] text-white text-sm font-bold hover:bg-[#92400E] hover:-translate-y-0.5 hover:shadow-lg transition-[background-color,transform,box-shadow] duration-300 disabled:opacity-60" data-testid="auth-submit">
                  {busy ? "Just a moment…" : mode === "login" ? "Sign In" : "Create My Account"}
                </button>
              </form>
              <p className="text-xs text-stone-400 text-center mt-6">By continuing you agree to our <Link to="/policies" className="underline">Terms & Privacy Policy</Link>.</p>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
